import React, { useState, useEffect } from 'react';
import { Plus, Filter, Search, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import PostCard from '../components/PostCard';
import CreatePostModal from '../components/CreatePostModal';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Community = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('All');
  const [sortBy, setSortBy] = useState('New'); // New sort filter
  const [userFilter, setUserFilter] = useState('All Posts'); // My Posts, My Comments, Saved
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const categoryFilters = ['All', 'Experience', 'Question', 'Advice', 'Reflection'];
  const sortOptions = ['New', 'Old'];
  const userFilters = ['All Posts', 'My Posts', 'My Comments', 'Saved'];

  useEffect(() => {
    fetchPosts();
  }, [activeFilter, searchQuery, sortBy, userFilter]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = {};
      
      // Category filter
      if (activeFilter !== 'All') params.post_type = activeFilter;
      
      // Search
      if (searchQuery) params.search = searchQuery;
      
      // User-specific filters
      if (userFilter === 'My Posts' && user) {
        params.author_id = user.id;
      }
      
      const response = await axios.get(`${API}/posts`, { params });
      let filteredPosts = response.data;
      
      // Client-side filtering for My Comments and Saved (backend doesn't support these yet)
      if (userFilter === 'My Comments' && user) {
        // Get posts where user has commented
        const userComments = await axios.get(`${API}/comments?user_id=${user.id}`);
        const postIdsWithComments = [...new Set(userComments.data.map(c => c.post_id))];
        filteredPosts = filteredPosts.filter(p => postIdsWithComments.includes(p.id));
      }
      
      if (userFilter === 'Saved' && user) {
        // TODO: Implement saved posts feature in backend
        // For now, show empty
        filteredPosts = [];
      }
      
      // Sort
      if (sortBy === 'Old') {
        filteredPosts = [...filteredPosts].reverse();
      }
      
      setPosts(filteredPosts);
    } catch (error) {
      toast({
        title: 'Failed to load posts',
        description: 'Please try refreshing the page',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = () => {
    if (!isAuthenticated) {
      toast({
        title: 'Login required',
        description: 'Please sign in to create a post',
        variant: 'destructive',
      });
      navigate('/login');
      return;
    }
    setShowCreateModal(true);
  };

  const handlePostCreated = (newPost) => {
    setPosts([newPost, ...posts]);
  };

  const handlePostUpdate = () => {
    fetchPosts(); // Refresh posts after update
  };

  const handlePostDelete = (postId) => {
    setPosts(posts.filter(p => p.id !== postId));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F9F9F9] to-white">
      {/* Hero Section with Image */}
      <div className="relative bg-gradient-to-r from-[#7B3F9E] to-[#A65AC8] text-white py-16 overflow-hidden" data-testid="community-hero">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://i0.wp.com/periwinkle-labs.com/wp-content/uploads/2026/01/carousal5b.jpg" 
            alt="Wellness"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            This space is for every woman
          </h1>
          <p className="text-xl mb-8 opacity-90">
            Share your experiences, ask questions, and support each other on the journey to better pelvic health.
          </p>
          <button
            onClick={handleCreatePost}
            className="bg-[#FF8C00] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#E67E00] transition inline-flex items-center space-x-2 shadow-lg"
            data-testid="create-post-hero-button"
          >
            <Plus className="w-5 h-5" />
            <span>Share Your Story</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search and Filters */}
        <div className="mb-8">
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Community Posts</h2>
          </div>

          {/* Search Bar */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search posts by title, content, or tags..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent outline-none transition"
              data-testid="search-posts-input"
            />
          </div>

          {/* Filter Bar - Responsive */}
          <div className="space-y-4">
            {/* Category Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-700 hidden sm:inline">Category:</span>
              {categoryFilters.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition ${
                    activeFilter === filter
                      ? 'bg-[#7B3F9E] text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                  data-testid={`filter-${filter.toLowerCase()}`}
                >
                  {filter}
                </button>
              ))}
            </div>

            {/* Sort & User Filters - Responsive Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Sort Dropdown */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sort by
                </label>
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full appearance-none px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent outline-none transition bg-white cursor-pointer"
                  >
                    {sortOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                </div>
              </div>

              {/* User Filter Dropdown */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filter posts
                </label>
                <div className="relative">
                  <select
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    className="w-full appearance-none px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent outline-none transition bg-white cursor-pointer"
                    disabled={!isAuthenticated && userFilter !== 'All Posts'}
                  >
                    {userFilters.map((option) => (
                      <option 
                        key={option} 
                        value={option}
                        disabled={!isAuthenticated && option !== 'All Posts'}
                      >
                        {option}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Active Filters Display */}
            {(activeFilter !== 'All' || sortBy !== 'New' || userFilter !== 'All Posts' || searchQuery) && (
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-200">
                <span className="text-sm text-gray-600">Active filters:</span>
                {activeFilter !== 'All' && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {activeFilter}
                    <button
                      onClick={() => setActiveFilter('All')}
                      className="ml-1 hover:text-purple-900"
                    >
                      ×
                    </button>
                  </span>
                )}
                {sortBy !== 'New' && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Sort: {sortBy}
                  </span>
                )}
                {userFilter !== 'All Posts' && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {userFilter}
                  </span>
                )}
                {searchQuery && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    Search: "{searchQuery}"
                    <button
                      onClick={() => setSearchQuery('')}
                      className="ml-1 hover:text-orange-900"
                    >
                      ×
                    </button>
                  </span>
                )}
                <button
                  onClick={() => {
                    setActiveFilter('All');
                    setSortBy('New');
                    setUserFilter('All Posts');
                    setSearchQuery('');
                  }}
                  className="text-xs text-gray-600 hover:text-gray-900 underline"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Posts Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#FF8C00] border-t-transparent"></div>
            <p className="mt-4 text-gray-600">Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm" data-testid="no-posts-message">
            <p className="text-gray-600 text-lg mb-4">
              No posts yet in this category.
            </p>
            <p className="text-gray-500">
              Be the first to share your {activeFilter !== 'All' ? activeFilter.toLowerCase() : 'story'}!
            </p>
          </div>
        ) : (
          <div className="grid gap-6" data-testid="posts-grid">
            {posts.map((post) => (
              <PostCard 
                key={post.id} 
                post={post} 
                onUpdate={handlePostUpdate}
                onDelete={handlePostDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onPostCreated={handlePostCreated}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Community;
